/**
 * NAV-5.1 / NAV-5.3 — fetch the numbers `commissionAreaCounts.ts` reduces.
 *
 * ⚠️ ASK AUTHORITY BEFORE READING. `countHeldLines` and the agreement reader
 * answer zero rows to a caller without `commission.view`, which is
 * indistinguishable from an account with nothing held and every agreement
 * signed. A badge of 0 from that path would be the empty-list defect.
 *
 * Failures stay `null`. The reducers hide a null badge rather than showing 0.
 */
import { useEffect, useMemo, useState } from 'react';

import { usePermissions } from '@/contexts/PermissionContext';
import { useTenant } from '@/contexts/TenantContext';
import { useCommissionCostingEnabled } from '@/hooks/useCommissionCostingEnabled';
import {
  EMPTY_COMMISSION_AREA_COUNTS,
  type CommissionAreaCounts,
} from '@/lib/commissionAreaCounts';
import { readinessGapCount } from '@/lib/commissionAccountReadiness';
import { splitAgreements } from '@/lib/commissionAgreements';
import {
  canSeeCommissionDestination,
  canSeeCommissionGroup,
  type CommissionNavViewer,
} from '@/lib/commissionAreaNav';
import {
  countHeldLines,
  fetchAccountReadiness,
  fetchAssignmentAgreements,
} from '@/services/commissionOpsHealthService';
import { hasCommissionAuthority } from '@/services/commissionPeriodsService';

const COUNTS_CHANGED_EVENT = 'joules:commission-area-counts-changed';

/**
 * Tell every mounted counts hook to re-read. The sidebar and the Overview each
 * hold their own instance, so a payment recorded in an Overview panel would
 * otherwise leave the sidebar badge saying what it said before.
 */
export function notifyCommissionAreaCountsChanged(): void {
  window.dispatchEvent(new Event(COUNTS_CHANGED_EVENT));
}

export function useCommissionNavViewer(): CommissionNavViewer {
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const { enabled: commissionEnabled, settingsLoading: commissionLoading } =
    useCommissionCostingEnabled();
  return useMemo(
    () => ({ permissionsLoading, commissionLoading, commissionEnabled, hasPermission }),
    [permissionsLoading, commissionLoading, commissionEnabled, hasPermission],
  );
}

export function useCommissionAreaCounts(): CommissionAreaCounts {
  const viewer = useCommissionNavViewer();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.tenant_id;
  const [counts, setCounts] = useState<CommissionAreaCounts>(EMPTY_COMMISSION_AREA_COUNTS);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const bump = () => setVersion((v) => v + 1);
    window.addEventListener(COUNTS_CHANGED_EVENT, bump);
    return () => window.removeEventListener(COUNTS_CHANGED_EVENT, bump);
  }, []);

  const showGroup = canSeeCommissionGroup(viewer);
  const showMoney = canSeeCommissionDestination('overview', viewer);
  const showSetup = canSeeCommissionDestination('setup', viewer);

  useEffect(() => {
    if (!showGroup || !tenantId) {
      setCounts(EMPTY_COMMISSION_AREA_COUNTS);
      return;
    }
    let cancelled = false;

    const load = async () => {
      const next: CommissionAreaCounts = { ...EMPTY_COMMISSION_AREA_COUNTS };

      if (showMoney) {
        try {
          const allowed = await hasCommissionAuthority('commission.view', tenantId);
          if (allowed) {
            const [held, agreements] = await Promise.all([
              countHeldLines(tenantId),
              fetchAssignmentAgreements(),
            ]);
            next.heldLines = held;
            next.unsignedAgreements = splitAgreements(agreements).outstanding.length;
          }
        } catch {
          next.heldLines = null;
          next.unsignedAgreements = null;
        }
      }

      if (showSetup) {
        try {
          const checks = await fetchAccountReadiness();
          next.setupGaps = readinessGapCount(checks);
        } catch {
          next.setupGaps = null;
        }
      }

      if (!cancelled) setCounts(next);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [showGroup, showMoney, showSetup, tenantId, version]);

  return counts;
}
